/**
 * SimplePortfolioSummary Component
 *
 * Enhanced portfolio summary supporting both:
 * 1. Quantity-based tracking (market value + gain/loss)
 * 2. Amount-only tracking (cost basis only)
 *
 * Shows different metrics based on portfolio composition
 */

import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { formatCurrency, formatLastUpdated } from '../utils/priceCalculations';
import { RefreshCw, Loader2, AlertCircle, PiggyBank, BarChart3, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';

export const SimplePortfolioSummary = ({
  totalCostBasis = 0,
  totalMarketValue = 0,
  totalGainLoss = 0,
  totalGainLossPercent = 0,
  investmentCount = 0,
  pricedCount = 0,
  unpricedCount = 0,
  trackableCount = 0,
  manualCount = 0,
  lastUpdated = null,
  loading = false,
  error = null,
  onRefresh,
  currency = 'GBP',
  className = ''
}) => {
  const hasGainLossData = trackableCount > 0 && totalMarketValue !== totalCostBasis;
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshing) return;

    setIsRefreshing(true);
    try {
      await onRefresh();
    } catch (err) {
      console.error('Error refreshing prices:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Card className={`p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Portfolio Summary</h2>
        </div>

        {/* Refresh Button */}
        {onRefresh && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <span className="ml-2 text-sm">Failed to load pricing data. Please try again.</span>
        </Alert>
      )}

      {/* Loading State */}
      {loading && !totalCostBasis ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-500 dark:text-gray-400">Loading portfolio...</span>
        </div>
      ) : (
        <>
          {/* Main Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Total Market Value */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {hasGainLossData ? 'Total Market Value' : 'Total Portfolio Value'}
                </span>
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {formatCurrency(totalMarketValue, currency, 2)}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {hasGainLossData ? 'Current market worth' : 'Total amount invested'}
              </p>
            </div>

            {/* Total Cost Basis (if different from market value) */}
            {hasGainLossData && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <PiggyBank className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cost Basis</span>
                </div>
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                  {formatCurrency(totalCostBasis, currency, 2)}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Total amount invested
                </p>
              </div>
            )}
          </div>

          {/* Gain/Loss Section */}
          {hasGainLossData && (
            <div className={`mb-6 p-4 rounded-lg border ${
              totalGainLoss >= 0
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {totalGainLoss >= 0 ? (
                    <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
                  ) : (
                    <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
                  )}
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Gain/Loss
                  </span>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${
                    totalGainLoss >= 0
                      ? 'text-green-700 dark:text-green-400'
                      : 'text-red-700 dark:text-red-400'
                  }`}>
                    {totalGainLoss >= 0 ? '+' : ''}{formatCurrency(totalGainLoss, currency, 2)}
                  </div>
                  <div className={`text-sm font-medium ${
                    totalGainLoss >= 0
                      ? 'text-green-600 dark:text-green-500'
                      : 'text-red-600 dark:text-red-500'
                  }`}>
                    {totalGainLoss >= 0 ? '+' : ''}{totalGainLossPercent.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats Footer - Simple count only */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium">{investmentCount}</span> investment{investmentCount !== 1 ? 's' : ''}
            </div>
            {/* Last Updated */}
            {lastUpdated && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Updated: <span className="font-medium">{formatLastUpdated(lastUpdated)}</span>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

export default SimplePortfolioSummary;
